<template>
  <div class="old-form-renderer" :class="{ 'is-readonly': readonly }">
    <div ref="containerRef" class="form-container" v-html="processedHtml"></div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { uploadAttach, getAttachDownloadUrl } from '@/api/attach'

const props = defineProps({
  htmlContent: { type: String, default: '' },
  jsContent: { type: String, default: '' },
  formData: { type: Object, default: () => ({}) },
  readonly: { type: Boolean, default: false },
  taskId: { type: [String, Number], default: '' }
})

const emit = defineEmits(['update'])

const containerRef = ref(null)

const processedHtml = computed(() => {
  let html = props.htmlContent || ''
  html = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
  return html
})

// ========== 工具函数 ==========

// 解析 formdatasrc / formdatatag：返回 { tableName, fieldName }
const parseDotAttr = (el, attrName) => {
  const src = (el.getAttribute(attrName) || '').toLowerCase().trim()
  if (!src) return null
  const dotIdx = src.indexOf('.')
  if (dotIdx <= 0) return null
  return {
    tableName: src.substring(0, dotIdx),
    fieldName: src.substring(dotIdx + 1),
    raw: src
  }
}

// 判断元素是否属于子表内
const isInsideGrid = (el) => {
  let p = el.parentElement
  while (p) {
    if (p.tagName === 'TABLE' && p.getAttribute('formgrid') === '1') return p
    p = p.parentElement
  }
  return null
}

// 构建数据查找表
const buildDataLookup = (data) => {
  const lookup = {}
  const detailRows = {}

  if (data.main) {
    Object.entries(data.main).forEach(([tableName, row]) => {
      if (row && typeof row === 'object') {
        Object.entries(row).forEach(([field, val]) => {
          lookup[`${tableName}.${field}`.toLowerCase()] = val
        })
      }
    })
  }

  if (data.detail) {
    Object.entries(data.detail).forEach(([tableName, rows]) => {
      if (Array.isArray(rows) && rows.length > 0) {
        detailRows[tableName.toLowerCase()] = rows
        const firstRow = rows[0]
        if (firstRow && typeof firstRow === 'object') {
          Object.entries(firstRow).forEach(([field, val]) => {
            lookup[`${tableName}.${field}`.toLowerCase()] = val
          })
        }
      }
    })
  }

  return { lookup, detailRows }
}

// ========== 赋值：填充表单 ==========

// 给单个元素赋值
const setElementValue = (el, value) => {
  const tag = el.tagName
  const type = (el.type || '').toLowerCase()
  const texttype = (el.getAttribute('texttype') || '').toLowerCase()

  // 跳过自动生成
  if (texttype === 'sn') return

  if (tag === 'INPUT') {
    if (type === 'checkbox') {
      el.checked = !!value || value === '1' || value === 'true'
    } else if (type === 'radio') {
      el.checked = String(el.value) === String(value)
    } else if (type === 'button' || type === 'submit' || type === 'reset' || type === 'file') {
      // 不填充按钮和文件
    } else {
      el.value = value != null ? String(value) : ''
    }
  } else if (tag === 'SELECT') {
    el.value = value != null ? String(value) : ''
  } else if (tag === 'TEXTAREA') {
    el.value = value != null ? String(value) : ''
  } else if (tag === 'DIV' && texttype === 'richtext') {
    el.innerHTML = value != null ? String(value) : ''
  } else if (tag === 'LABEL' || tag === 'A' || tag === 'P' || tag === 'SPAN' || tag === 'TD') {
    // 只有没有子控件（input/select/textarea）时才设置文本
    if (!el.querySelector('input, select, textarea')) {
      el.textContent = value != null ? String(value) : ''
    }
  }
}

// 填充所有元素（带 formdatasrc 属性）
const fillFormData = () => {
  if (!containerRef.value || !props.formData) return
  const data = props.formData
  if (typeof data === 'string') {
    try { fillFormDataFromObject(JSON.parse(data)) } catch (e) { console.warn('[OldFormRenderer] JSON解析失败:', e) }
    return
  }
  fillFormDataFromObject(data)
}

const fillFormDataFromObject = (data) => {
  if (!data || typeof data !== 'object') return
  if (!containerRef.value) return

  const { lookup, detailRows } = buildDataLookup(data)
  console.log('[OldFormRenderer] lookup keys:', Object.keys(lookup).slice(0, 20), '共', Object.keys(lookup).length)
  console.log('[OldFormRenderer] detailRows 表名:', Object.keys(detailRows))

  let filledCount = 0

  // ====== 1. 填充常规控件（不在子表内） ======
  // 查找所有带 formdatasrc 的元素（不只是 input/select/textarea）
  const allElements = containerRef.value.querySelectorAll('[formdatasrc]')
  
  allElements.forEach(el => {
    // 跳过子表内的控件
    if (isInsideGrid(el)) return

    const parsed = parseDotAttr(el, 'formdatasrc')
    if (!parsed) return

    const key = `${parsed.tableName}.${parsed.fieldName}`
    if (key in lookup) {
      setElementValue(el, lookup[key])
      filledCount++
    }
  })
  console.log('[OldFormRenderer] 常规控件填充数:', filledCount)

  // ====== 2. 填充子表数据 ======
  let gridFilledCount = 0
  const gridTables = containerRef.value.querySelectorAll('table[formgrid="1"]')
  console.log('[OldFormRenderer] 子表数量:', gridTables.length)

  gridTables.forEach(table => {
    const bindTable = (table.getAttribute('formbindtable') || table.getAttribute('name') || table.id || '').toLowerCase()
    if (!bindTable) return

    const rows = detailRows[bindTable]
    if (!Array.isArray(rows) || rows.length === 0) {
      console.log('[OldFormRenderer] 子表', bindTable, '无数据')
      return
    }

    const headRows = parseInt(table.getAttribute('gridheadrows') || '1')
    const footerRows = parseInt(table.getAttribute('footerrowcount') || '0')
    const tbody = table.querySelector('tbody') || table
    let trs = tbody.querySelectorAll('tr')

    const totalDataRows = trs.length - headRows - footerRows
    console.log(`[OldFormRenderer] 子表 ${bindTable}: 数据行=${rows.length}, HTML行=${trs.length}, 表头=${headRows}, 页脚=${footerRows}, 数据模板行=${totalDataRows}`)

    // 动态克隆行
    if (rows.length > totalDataRows && totalDataRows > 0) {
      const templateRow = trs[headRows]
      const insertBefore = footerRows > 0 ? trs[trs.length - footerRows] : null
      
      for (let i = totalDataRows; i < rows.length; i++) {
        const newRow = templateRow.cloneNode(true)
        newRow.querySelectorAll('[formdatasrc]').forEach(el => setElementValue(el, ''))
        newRow.querySelectorAll('input[type="checkbox"]').forEach(el => { el.checked = false })
        if (insertBefore) {
          tbody.insertBefore(newRow, insertBefore)
        } else {
          tbody.appendChild(newRow)
        }
      }
      trs = tbody.querySelectorAll('tr')
    }

    // 填充每行
    rows.forEach((rowData, ri) => {
      const trIndex = headRows + ri
      if (trIndex >= trs.length - footerRows) return

      const rowEl = trs[trIndex]
      rowEl.querySelectorAll('[formdatasrc]').forEach(el => {
        const parsed = parseDotAttr(el, 'formdatasrc')
        if (!parsed || parsed.tableName !== bindTable) return

        if (rowData[parsed.fieldName] !== undefined) {
          setElementValue(el, rowData[parsed.fieldName])
          gridFilledCount++
        }
      })
    })
  })
  console.log('[OldFormRenderer] 子表控件填充数:', gridFilledCount)

  // ====== 3. 设置只读状态 ======
  if (props.readonly) {
    containerRef.value.querySelectorAll('input, select, textarea').forEach(el => {
      if (el.type === 'button' || el.type === 'submit' || el.type === 'reset') return
      if (el.tagName === 'SELECT' || el.type === 'checkbox' || el.type === 'radio') {
        el.disabled = true
      } else if (el.type !== 'file') {
        el.readOnly = true
      }
    })
  }
}

// ========== 回写：收集表单数据 ==========

// 获取元素值
const getElementValue = (el) => {
  const tag = el.tagName
  const type = (el.type || '').toLowerCase()
  const texttype = (el.getAttribute('texttype') || '').toLowerCase()

  if (tag === 'INPUT') {
    if (type === 'checkbox') return el.checked ? (el.value || '1') : ''
    if (type === 'radio') return el.checked ? (el.value || '') : ''
    if (type === 'file') return ''
    return el.value || ''
  } else if (tag === 'SELECT') {
    return el.value || ''
  } else if (tag === 'TEXTAREA') {
    return el.value || ''
  } else if (tag === 'DIV' && texttype === 'richtext') {
    return el.innerHTML || ''
  } else if (tag === 'LABEL' || tag === 'A' || tag === 'P' || tag === 'SPAN' || tag === 'TD') {
    if (!el.querySelector('input, select, textarea')) {
      return el.textContent || ''
    }
  }
  return ''
}

const collectFormData = () => {
  if (!containerRef.value) return { main: {}, detail: {} }
  // 视图模式不需要回写
  if (props.readonly) return { main: {}, detail: {} }

  const result = { main: {}, detail: {} }

  // ====== 1. 收集常规控件（不在子表内，有 formdatatag） ======
  containerRef.value.querySelectorAll('[formdatatag]').forEach(el => {
    if (isInsideGrid(el)) return

    const tag = el.tagName
    const type = (el.type || '').toLowerCase()
    
    // 跳过附件/图片按钮（type=button, 其值存储在 formData.main 中）
    if (tag === 'INPUT' && (type === 'button' || type === 'submit' || type === 'reset' || type === 'file')) return
    // 跳过未选中的 radio/checkbox
    if (tag === 'INPUT' && (type === 'radio' || type === 'checkbox') && !el.checked) return

    const parsed = parseDotAttr(el, 'formdatatag')
    if (!parsed) return

    const tn = parsed.tableName
    if (!result.main[tn]) result.main[tn] = {}
    result.main[tn][parsed.fieldName] = getElementValue(el)
  })

  // ====== 1b. 收集附件/图片字段（从 formData.main 中读取，这些字段不通过 DOM 收集） ======
  const mainData = props.formData?.main || {}
  for (const [tableName, row] of Object.entries(mainData)) {
    if (!row || typeof row !== 'object') continue
    for (const [col, val] of Object.entries(row)) {
      // 只有当值是 JSON 数组（附件格式）时才覆盖（其他字段已由 DOM 收集）
      if (typeof val === 'string' && val.startsWith('[{')) {
        if (!result.main[tableName]) result.main[tableName] = {}
        result.main[tableName][col] = JSON.parse(val)
      }
    }
  }

  // ====== 2. 收集子表数据 ======
  const gridTables = containerRef.value.querySelectorAll('table[formgrid="1"]')
  gridTables.forEach(table => {
    const bindTable = (table.getAttribute('formbindtable') || table.getAttribute('name') || table.id || '').toLowerCase()
    if (!bindTable) return

    const headRows = parseInt(table.getAttribute('gridheadrows') || '1')
    const footerRows = parseInt(table.getAttribute('footerrowcount') || '0')
    const tbody = table.querySelector('tbody') || table
    const trs = tbody.querySelectorAll('tr')

    const gridRows = []
    for (let i = headRows; i < trs.length - footerRows; i++) {
      const row = {}
      trs[i].querySelectorAll('[formdatatag]').forEach(el => {
        const parsed = parseDotAttr(el, 'formdatatag')
        if (!parsed || parsed.tableName !== bindTable) return
        row[parsed.fieldName] = getElementValue(el)
      })
      if (Object.keys(row).length > 0) gridRows.push(row)
    }

    if (gridRows.length > 0) {
      result.detail[bindTable] = gridRows
    }
  })

  console.log('[OldFormRenderer] 收集数据:', result)
  return result
}

// ========== 事件绑定 ==========

const bindFormEvents = () => {
  if (!containerRef.value) return

  containerRef.value.querySelectorAll('input, select, textarea').forEach(el => {
    if (el.type === 'button' || el.type === 'submit' || el.type === 'reset' || el.type === 'file') return

    const parsed = parseDotAttr(el, 'formdatatag') || parseDotAttr(el, 'formdatasrc')
    if (!parsed) return

    const handler = () => {
      emit('update', {
        tableName: parsed.tableName,
        fieldName: parsed.fieldName,
        value: getElementValue(el),
        isGrid: !!isInsideGrid(el)
      })
    }

    if (el.tagName === 'SELECT') {
      el.addEventListener('change', handler)
    } else if (el.type === 'checkbox' || el.type === 'radio') {
      el.addEventListener('change', handler)
    } else {
      el.addEventListener('input', handler)
    }
  })
}

// ========== 附件/图片处理 ==========

// 判断是否为图片扩展名
const isImageExt = (ext) => ['.jpg', '.jpeg', '.png', '.gif', '.bmp'].includes(ext?.toLowerCase())

const getFileEmoji = (ext) => {
  const map = {
    '.doc': '📝', '.docx': '📝', '.xls': '📊', '.xlsx': '📊',
    '.ppt': '📽', '.pptx': '📽', '.pdf': '📄', '.txt': '📃',
    '.zip': '📦', '.rar': '📦', '.7z': '📦', '.mp4': '🎬', '.mp3': '🎵'
  }
  return map[ext?.toLowerCase()] || '📎'
}

// 从 SYSAttachList 附件数据构造下载 URL
const buildSysAttachDownloadUrl = (attach) => {
  // 优先用 guid 通过 API 下载
  const guid = attach.Guid || attach.guid
  if (guid) return getAttachDownloadUrl(guid)
  // 旧系统附件：用 phyName 构建直接路径
  const phyName = attach.PhyName || attach.phyName || attach.pname || ''
  // 匹配旧系统路径: CloudSpaces/{tenant}/Workflow/TaskBusiness/Form/{phyName}
  return `/api/attach/download-sys?phyName=${encodeURIComponent(phyName)}`
}

// 从业务字段 JSON 解析附件列表
const parseFieldAttaches = (fieldValue) => {
  if (!fieldValue) return []
  if (Array.isArray(fieldValue)) return fieldValue
  if (typeof fieldValue === 'string') {
    try { const arr = JSON.parse(fieldValue); return Array.isArray(arr) ? arr : [] }
    catch { return [] }
  }
  return []
}

// 附件 JSON 序列化
const serializeFieldAttaches = (attaches) => {
  return JSON.stringify(attaches)
}

// 渲染单个附件（匹配旧系统 HTML 格式）
const renderOneAttach = (container, attach, btn, isImg, fieldName) => {
  const dspName = attach.DisplayName || attach.DspName || attach.dspName || attach.dspname || ''
  const phyName = attach.PhysicalName || attach.PhyName || attach.phyName || attach.pname || ''
  const fileType = attach.ExtensionName || attach.FileType || attach.fileType || attach.ename || ''
  const fileSize = attach.Size || attach.FileSize || attach.fileSize || attach.size || 0
  const downloadUrl = buildSysAttachDownloadUrl(attach)

  const wrapper = document.createElement('div')
  wrapper.className = 'old-attach-wrapper'
  wrapper.style.cssText = 'width:100%;overflow:hidden;margin:2px 0;'

  if (isImg && isImageExt(fileType)) {
    // 图片格式
    wrapper.setAttribute('divtype', 'img')
    const img = document.createElement('img')
    img.setAttribute('dspname', dspName)
    img.setAttribute('pname', phyName)
    img.setAttribute('ename', fileType)
    img.src = downloadUrl
    const jpgw = btn.getAttribute('jpgwidth') || '40'
    const jpgh = btn.getAttribute('jpgheight') || '40'
    img.style.cssText = `vertical-align:bottom;padding-left:5px;float:left;width:${jpgw}px;height:${jpgh}px;cursor:pointer;`
    img.onclick = () => { const w = window.open(downloadUrl, '_blank'); if (w) w.document.title = dspName }
    wrapper.appendChild(img)

    if (!props.readonly && btn.getAttribute('isdelete') === 'true') {
      const delWrap = document.createElement('div')
      delWrap.style.cssText = 'padding-left:5px;float:left;'
      const delSpan = document.createElement('span')
      delSpan.setAttribute('spantype', 'bus')
      delSpan.className = 'busattach_del'
      delSpan.setAttribute('physicalname', phyName)
      delSpan.textContent = '×'
      delSpan.style.cssText = 'cursor:pointer;color:red;font-size:16px;line-height:20px;'
      delSpan.onclick = (e) => { e.preventDefault(); removeAttachFromField(container, btn, isImg, fieldName, phyName) }
      delWrap.appendChild(delSpan)
      wrapper.appendChild(delWrap)
    }
  } else {
    // 普通附件格式
    wrapper.setAttribute('divtype', 'attachment')
    const leftDiv = document.createElement('div')
    leftDiv.style.cssText = 'float:left;line-height:20px;'

    const iconA = document.createElement('a')
    iconA.className = 'line_none'
    iconA.style.cssText = 'float:left;text-decoration:none;'
    iconA.setAttribute('download', dspName)
    iconA.href = downloadUrl
    const icon = document.createElement('i')
    icon.className = 'iconfont file_icon'
    icon.style.cssText = 'font-style:normal;font-size:16px;color:#1890ff;'
    icon.textContent = getFileEmoji(fileType)
    iconA.appendChild(icon)
    leftDiv.appendChild(iconA)

    const nameA = document.createElement('a')
    nameA.setAttribute('atype', 'bus')
    nameA.style.cssText = 'float:left;padding-left:3px;text-decoration:none;color:#333;cursor:pointer;'
    nameA.setAttribute('src', downloadUrl)
    nameA.setAttribute('dspname', dspName)
    nameA.setAttribute('pname', phyName)
    nameA.setAttribute('size', String(fileSize))
    nameA.setAttribute('ename', fileType)
    nameA.title = dspName
    const nameSpan = document.createElement('span')
    nameSpan.className = 'yulan'
    nameSpan.textContent = dspName
    nameA.appendChild(nameSpan)
    nameA.onclick = (e) => { e.preventDefault(); window.open(downloadUrl, '_blank') }
    leftDiv.appendChild(nameA)
    wrapper.appendChild(leftDiv)

    if (!props.readonly && btn.getAttribute('isdelete') === 'true') {
      const delWrap = document.createElement('div')
      delWrap.style.cssText = 'padding-left:5px;float:left;'
      const delSpan = document.createElement('span')
      delSpan.setAttribute('spantype', 'bus')
      delSpan.className = 'busattach_del'
      delSpan.setAttribute('physicalname', phyName)
      delSpan.textContent = '×'
      delSpan.style.cssText = 'cursor:pointer;color:red;font-size:16px;line-height:20px;'
      delSpan.onclick = (e) => { e.preventDefault(); removeAttachFromField(container, btn, isImg, fieldName, phyName) }
      delWrap.appendChild(delSpan)
      wrapper.appendChild(delWrap)
    }
  }

  container.appendChild(wrapper)
}

// 从 formData.main 中定位并设置字段值
const setFormDataFieldValue = (fieldName, value) => {
  const key = fieldName.toLowerCase()
  const mainData = props.formData?.main || {}
  for (const [tableName, row] of Object.entries(mainData)) {
    if (row && typeof row === 'object') {
      for (const col of Object.keys(row)) {
        if (col.toLowerCase() === key) {
          row[col] = value
          return true
        }
      }
    }
  }
  return false
}

// 从业务字段的附件列表中删除一项
const removeAttachFromField = async (container, btn, isImg, fieldName, phyName) => {
  if (!await $confirm('确定删除该附件？')) return
  const lookup = buildFormDataLookup()
  const fieldValue = lookup[fieldName.toLowerCase()]
  let attaches = parseFieldAttaches(fieldValue)
  const matchKey = phyName?.toLowerCase()
  attaches = attaches.filter(a => {
    const pn = (a.PhysicalName || a.PhyName || a.phyName || a.pname || '').toLowerCase()
    return pn !== matchKey
  })
  setFormDataFieldValue(fieldName, serializeFieldAttaches(attaches))
  container.querySelectorAll('.old-attach-wrapper').forEach(el => el.remove())
  attaches.forEach(a => renderOneAttach(container, a, btn, isImg, fieldName))
}

// 构建 formData 的扁平 lookup（与 fillFormData 保持一致）
const buildFormDataLookup = () => {
  const lookup = {}
  const mainData = props.formData?.main || {}
  for (const [tableName, row] of Object.entries(mainData)) {
    if (row && typeof row === 'object') {
      for (const [col, val] of Object.entries(row)) {
        lookup[col.toLowerCase()] = val
      }
    }
  }
  return lookup
}

// 渲染附件列表（从 formData 读取）
const renderFieldAttaches = (container, btn, isImg, fieldName) => {
  const lookup = buildFormDataLookup()
  console.log('[OldFormRenderer] lookup keys:', Object.keys(lookup))
  let fieldValue = lookup[fieldName.toLowerCase()]
  console.log('[OldFormRenderer] 字段原始值:', fieldName, '=', fieldValue, '类型:', typeof fieldValue)
  const attaches = parseFieldAttaches(fieldValue)
  console.log('[OldFormRenderer] 字段', fieldName, '附件数据:', attaches)
  container.querySelectorAll('.old-attach-wrapper').forEach(el => el.remove())
  if (attaches.length > 0) {
    attaches.forEach(a => renderOneAttach(container, a, btn, isImg, fieldName))
  }
}

// 初始化附件按钮
const initAttachButtons = async () => {
  if (!containerRef.value) return
  if (!props.taskId && Object.keys(props.formData || {}).length === 0) return

  const attachBtns = containerRef.value.querySelectorAll('input[buttontype="attachment"], input[buttontype="img"]')
  if (attachBtns.length === 0) return

  for (const btn of attachBtns) {
    const isImg = btn.getAttribute('buttontype') === 'img'
    
    // 读取字段绑定：formdatasrc="ut_260705.ta011" → fieldName="ta011"
    const formdatasrc = btn.getAttribute('formdatasrc') || btn.getAttribute('formdatatag') || ''
    const fieldName = formdatasrc.split('.').pop()?.toLowerCase() || ''
    console.log('[OldFormRenderer] 附件按钮 fieldName:', fieldName, 'formdatasrc:', formdatasrc)

    const pParent = btn.closest('p')
    const listContainer = document.createElement('div')
    listContainer.style.cssText = 'padding-left:5px;'
    if (pParent) {
      pParent.parentNode?.insertBefore(listContainer, pParent.nextSibling)
    } else {
      btn.parentNode?.insertBefore(listContainer, btn.nextSibling)
    }

    // 渲染已有附件（从 formData 字段读取）
    if (fieldName) {
      renderFieldAttaches(listContainer, btn, isImg, fieldName)
    }

    // 上传处理
    if (!props.readonly) {
      const fileInput = document.createElement('input')
      fileInput.type = 'file'
      fileInput.style.display = 'none'
      if (isImg) fileInput.accept = 'image/*'
      fileInput.multiple = true
      listContainer.appendChild(fileInput)

      btn.type = 'button'
      btn.onclick = () => { fileInput.click() }

      fileInput.onchange = async () => {
        const files = fileInput.files
        if (!files || files.length === 0) return

        // 读取现有附件列表（通过 lookup）
        const lookup = buildFormDataLookup()
        const curValue = lookup[fieldName.toLowerCase()]
        let attaches = parseFieldAttaches(curValue)

        for (const file of files) {
          try {
            const fd = new FormData()
            fd.append('file', file)
            fd.append('savePath', 'Workflow/TaskBusiness/Form')
            fd.append('fromGuid', String(props.taskId))

            const res = await uploadAttach(fd)
            const result = res.data || res.Data || res
            if (result && (result.ResultType !== false || result.resultType !== false)) {
              const meta = result.data || result.Data || result
              const newAttach = {
                DisplayName: file.name,
                PhysicalName: meta?.PhyName || meta?.phyName || '',
                Size: file.size,
                ExtensionName: '.' + file.name.split('.').pop()?.toLowerCase(),
                Guid: meta?.Guid || meta?.guid || ''
              }
              attaches.push(newAttach)
            }
          } catch (err) {
            console.warn('[OldFormRenderer] 上传附件失败:', err)
          }
        }
        fileInput.value = ''

        setFormDataFieldValue(fieldName, serializeFieldAttaches(attaches))
        containerRef.value && renderFieldAttaches(listContainer, btn, isImg, fieldName)
      }
    }
  }
}

// ========== 初始化 ==========

const initForm = () => {
  if (!containerRef.value) return
  nextTick(() => {
    fillFormData()
    bindFormEvents()
    initAttachButtons()
  })
}

watch(() => props.htmlContent, () => { initForm() })
watch(() => props.formData, () => { fillFormData() }, { deep: true })
watch(() => props.readonly, () => { initForm() })
onMounted(() => { initForm() })

defineExpose({ collectFormData })
</script>

<style scoped>
.old-form-renderer {
  width: 100%;
  min-height: 100px;
}

.form-container {
  padding: 16px;
  background: #fff;
  border-radius: 8px;
}

.form-container :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 2px 0;
  font-size: 12px;
  font-family: Arial, Verdana, "宋体";
  border: 1px solid #D4D6D5;
}

.form-container :deep(table td),
.form-container :deep(table th) {
  border: 1px solid #528da9;
  padding: 4px 8px;
  vertical-align: middle;
  text-align: left;
  min-height: 23px;
  line-height: 20px;
}

.form-container :deep(tr[style*="display: none"]),
.form-container :deep(tr[style*="display:none"]),
.form-container :deep(td[style*="display: none"]),
.form-container :deep(td[style*="display:none"]) {
  display: none !important;
}

.form-container :deep(table th) {
  background-color: #F1F1F1;
  font-weight: normal;
  color: #374151;
  text-align: center;
  white-space: nowrap;
  padding-left: 16px;
}

.form-container :deep(input[type="text"]),
.form-container :deep(input[type="number"]),
.form-container :deep(input[type="email"]),
.form-container :deep(input[type="tel"]),
.form-container :deep(input[type="date"]),
.form-container :deep(input[type="datetime-local"]),
.form-container :deep(input[type="password"]),
.form-container :deep(input[type="url"]),
.form-container :deep(input[type="search"]) {
  height: 100%;
  border: none;
  background: none;
  color: #333;
  font-size: 12px;
  text-indent: 2px;
  padding: 4px 3px;
  margin-bottom: 0;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  font-family: Arial, Verdana, "宋体";
}

.form-container :deep(select) {
  border: none;
  font-size: 12px;
  cursor: pointer;
  height: 20px;
  background: #fff;
  outline: none;
  padding: 0 2px;
  font-family: Arial, Verdana, "宋体";
}

.form-container :deep(textarea) {
  overflow: auto;
  border: none;
  padding: 0 1px;
  font-size: 12px;
  font-family: Arial, Verdana, "宋体";
  outline: none;
  resize: vertical;
  width: 100%;
  box-sizing: border-box;
  background: #fff;
  min-height: 40px;
}

.form-container :deep(input[readonly]),
.form-container :deep(textarea[readonly]) {
  background: #f5f5f5;
  color: #666;
  cursor: default;
}

.form-container :deep(input:disabled),
.form-container :deep(select:disabled),
.form-container :deep(textarea:disabled) {
  background: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}

.form-container :deep(input[type="checkbox"]),
.form-container :deep(input[type="radio"]) {
  cursor: pointer;
  margin: 0 4px;
  vertical-align: middle;
}

.form-container :deep(label) {
  font-size: 12px;
  color: #333;
  cursor: pointer;
  font-family: Arial, Verdana, "宋体";
}

.form-container :deep(input[type="button"]),
.form-container :deep(button) {
  cursor: pointer;
  font-size: 12px;
  padding: 4px 12px;
  font-family: Arial, Verdana, "宋体";
}

.form-container :deep(input[type="file"]) {
  padding: 4px;
  font-size: 12px;
}

.form-container :deep(span) {
  font-size: 13px;
  color: #374151;
}

.form-container :deep(a) {
  color: #10b981;
  text-decoration: none;
}

.form-container :deep(a:hover) {
  text-decoration: underline;
}

.form-container :deep(hr) {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 12px 0;
}

.form-container :deep(img) {
  max-width: 100%;
  height: auto;
}

.form-container :deep(p) {
  margin: 4px 0;
  font-size: 13px;
  line-height: 1.5;
}

.form-container :deep(input[type="hidden"]),
.form-container :deep(input[texttype="hidden"]) {
  display: none !important;
}

.form-container :deep([style*="display: none"]),
.form-container :deep([style*="display:none"]) {
  display: none !important;
}

.form-container :deep([style*="visibility: hidden"]) {
  visibility: hidden !important;
}

.form-container :deep(.hidden),
.form-container :deep(.hide),
.form-container :deep(.d-none) {
  display: none !important;
}

.form-container :deep(.invisible) {
  visibility: hidden !important;
}

.is-readonly .form-container :deep(input),
.is-readonly .form-container :deep(select),
.is-readonly .form-container :deep(textarea) {
  background: #f9fafb;
  border-color: #e5e7eb;
}

.form-container :deep(table[formgrid="1"]) {
  border: 2px solid #10b981;
  border-radius: 4px;
  overflow: hidden;
}

.form-container :deep(table[formgrid="1"] th) {
  background: #ecfdf5;
  border-bottom: 2px solid #10b981;
}

.form-container :deep(table thead th) {
  background: #f3f4f6;
  font-weight: 600;
}
</style>
